import { useMemo } from 'react';
import { computeJobCardTotals } from '@jobos/shared';
import type { JobCardPayload } from '@jobos/shared';
import type { JobCardCostRow } from '@jobos/shared';
import { formatCurrency } from '@/lib/utils';

/**
 * An HTML approximation of the workbook's JOB CARD sheet.
 *
 * Rendered from the exact payload handed to the Excel generator, with the sheet's
 * formulas reimplemented in `computeJobCardTotals` — the generated .xlsx carries no
 * cached formula results, so these numbers only exist once Excel recalculates.
 * Layout is deliberately close to the sheet's reading order, not pixel-identical.
 */
interface JobCardSheetPreviewProps {
  payload: JobCardPayload;
}

function HeaderField({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      {/* Never clip: addresses and job descriptions wrap in full rather than
          truncating behind a tooltip — the whole point is to read the card. */}
      <div className="whitespace-pre-wrap break-words text-sm">
        {value || <span className="text-muted-foreground">—</span>}
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-b bg-muted/50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide">
      {children}
    </div>
  );
}

/** One costed row: label, rate, quantity, cost. Blank cells stay blank, as on the sheet. */
function CostRows({ rows }: { rows: JobCardCostRow[] }) {
  return (
    <>
      {rows.map((row, i) => (
        <tr key={`${row.label}-${i}`} className="border-b last:border-0">
          <td className="px-3 py-1">{row.label}</td>
          <td className="px-3 py-1 text-right tabular-nums text-muted-foreground">
            {row.rate === null ? '' : formatCurrency(row.rate)}
          </td>
          <td className="px-3 py-1 text-right tabular-nums text-muted-foreground">
            {row.quantity === null ? '' : `${row.quantity}${row.quantityUnit ? ` ${row.quantityUnit}` : ''}`}
          </td>
          <td className="px-3 py-1 text-right tabular-nums">
            {row.quantity === null && row.cost === 0 && row.rate !== null ? '' : formatCurrency(row.cost)}
          </td>
        </tr>
      ))}
    </>
  );
}

function TotalRow({ label, amount }: { label: string; amount: number }) {
  return (
    <tr className="border-t bg-muted/30 font-semibold">
      <td className="px-3 py-1.5" colSpan={3}>
        <span className="float-right">{label}</span>
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums">{formatCurrency(amount)}</td>
    </tr>
  );
}

function CostTable({ children }: { children: React.ReactNode }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b text-[10px] uppercase tracking-wide text-muted-foreground">
          <th className="px-3 py-1 text-left font-medium">Item</th>
          <th className="w-24 px-3 py-1 text-right font-medium">Rate</th>
          <th className="w-24 px-3 py-1 text-right font-medium">Qty</th>
          <th className="w-28 px-3 py-1 text-right font-medium">Cost</th>
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}

export function JobCardSheetPreview({ payload }: JobCardSheetPreviewProps) {
  const totals = useMemo(() => computeJobCardTotals(payload), [payload]);
  const { header } = payload;

  return (
    <div className="space-y-4">
      {/* Header block — rows 2-5 */}
      <div className="rounded-md border">
        <SectionTitle>Newman General Construction Job Card</SectionTitle>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 p-3 md:grid-cols-4">
          <HeaderField label="Name" value={header.clientName} />
          <HeaderField label="Contact" value={header.managerName} />
          <HeaderField label="Start Date" value={header.startDate} />
          <HeaderField label="Completed" value={header.completedDate} />
          <HeaderField label="Billing Address" value={header.billingAddress} />
          <HeaderField label="File Location" value={header.fileLocation} />
          <HeaderField label="Service Address" value={header.serviceAddress} />
          <HeaderField label="Cash Y/N" value={header.cashYN} />
        </div>
        <div className="border-t p-3">
          <HeaderField label="Job" value={header.jobDescription} />
        </div>
      </div>

      {/* Labor — rows 10-15 */}
      <div className="overflow-hidden rounded-md border">
        <SectionTitle>Labor (not reimbursement)</SectionTitle>
        <CostTable>
          <CostRows rows={totals.laborRows} />
          <TotalRow label="Total" amount={totals.laborTotal} />
        </CostTable>
      </div>

      {/* Parts / materials — rows 18-26 */}
      <div className="overflow-hidden rounded-md border">
        <SectionTitle>Parts / Materials (reimbursement)</SectionTitle>
        <CostTable>
          <CostRows rows={totals.reimbursementRows} />
          <TotalRow label="Total" amount={totals.reimbursementTotal} />
        </CostTable>
      </div>

      {/* Workers — rows 29-37 */}
      <div className="overflow-hidden rounded-md border">
        <SectionTitle>Workers</SectionTitle>
        <CostTable>
          <CostRows rows={totals.workerRows} />
          <tr className="border-b last:border-0">
            <td className="px-3 py-1" colSpan={3}>
              Work Comp, Insur and Other Fees
            </td>
            <td className="px-3 py-1 text-right tabular-nums">{formatCurrency(totals.workComp)}</td>
          </tr>
          <TotalRow label="Total" amount={totals.workerTotal} />
        </CostTable>
        {totals.workerOverflow.length > 0 && (
          <p className="border-t px-3 py-2 text-xs text-destructive">
            Only 6 workers fit on the sheet. Not included:{' '}
            {totals.workerOverflow.map((w) => `${w.name} (${w.hours}h)`).join(', ')}
          </p>
        )}
      </div>

      {/* Misc / other expenses — rows 54-71 */}
      <div className="overflow-hidden rounded-md border">
        <SectionTitle>Misc / Other Expenses</SectionTitle>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-[10px] uppercase tracking-wide text-muted-foreground">
              <th className="px-3 py-1 text-left font-medium">Description</th>
              <th className="w-28 px-3 py-1 text-right font-medium">Cost</th>
              <th className="w-28 px-3 py-1 text-left font-medium">Paid Date</th>
              <th className="w-24 px-3 py-1 text-left font-medium">Check #</th>
              <th className="w-24 px-3 py-1 text-left font-medium">Reimburse</th>
            </tr>
          </thead>
          <tbody>
            {totals.inlineExpenses.length === 0 && (
              <tr className="border-b">
                <td className="px-3 py-2 text-muted-foreground" colSpan={5}>
                  No expenses on this job.
                </td>
              </tr>
            )}
            {totals.inlineExpenses.map((expense, i) => (
              <tr key={`${expense.description}-${i}`} className="border-b">
                <td className="break-words px-3 py-1">{expense.description}</td>
                <td className="px-3 py-1 text-right tabular-nums">{formatCurrency(expense.cost)}</td>
                <td className="px-3 py-1">{expense.paidDate}</td>
                <td className="px-3 py-1">{expense.checkNumber}</td>
                <td className="px-3 py-1">{expense.reimburse}</td>
              </tr>
            ))}
            {totals.expensesOverflowCount > 0 && (
              <tr className="border-b">
                <td className="px-3 py-1 italic text-muted-foreground" colSpan={5}>
                  + {totals.expensesOverflowCount} more — see 'Expenses' sheet
                </td>
              </tr>
            )}
            <tr className="border-t bg-muted/30 font-semibold">
              <td className="px-3 py-1.5 text-right">Total</td>
              <td className="px-3 py-1.5 text-right tabular-nums">
                {formatCurrency(totals.expensesTotal)}
              </td>
              <td colSpan={3} />
            </tr>
          </tbody>
        </table>
      </div>

      {/* Totals — rows 43-48 */}
      <div className="overflow-hidden rounded-md border">
        <SectionTitle>Totals</SectionTitle>
        <table className="w-full text-sm">
          <tbody>
            <tr className="border-b">
              <td className="px-3 py-1.5 font-medium">Total Parts/Labor/Misc Expenses</td>
              <td className="w-40 px-3 py-1.5 text-right font-semibold tabular-nums">
                {formatCurrency(totals.grandTotal)}
              </td>
            </tr>
            <tr className="border-b">
              <td className="px-3 py-1.5">Breakdown Total (REF)</td>
              <td className="px-3 py-1.5 text-right tabular-nums">
                {formatCurrency(totals.breakdownTotal)}
              </td>
            </tr>
            <tr className="border-b">
              <td className="px-3 py-1.5">HR and Breakdown Total</td>
              <td className="px-3 py-1.5 text-right tabular-nums">
                {formatCurrency(totals.breakdownTotal)}
              </td>
            </tr>
            {/*
              Both are blank on purpose. G46 is typed by hand in Excel and is
              never written by the generator, so G48 (= G46 - G43 - K43) has
              nothing to compute from either. Showing an em dash beats inventing
              a number.
            */}
            <tr className="border-b">
              <td className="px-3 py-1.5">Invoice / Contract Price</td>
              <td className="px-3 py-1.5 text-right text-muted-foreground">—</td>
            </tr>
            <tr>
              <td className="px-3 py-1.5">Total Profit</td>
              <td className="px-3 py-1.5 text-right text-muted-foreground">—</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
